import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Handles legacy Vue/page.js URLs:
 *   /?home           → /home
 *   /?activity-reports → /activity-reports
 *   /?case-reports   → /case-reports
 *   /?calls-for-service → /calls-for-service
 *   /?analytics-reports → /analytics-reports
 *   /?signout        → sign out + /login
 *   /index.html      → /
 *   /registration.html?=xxx → /registration/xxx
 *   /reset-password.html?=xxx → /reset-password/xxx
 */
export function LegacyUrlRedirect() {
  const location = useLocation();
  const { isAuthenticated, signOut } = useAuth();

  const search = location.search;
  const pathname = location.pathname;

  // Handle /registration.html?=xxx
  if (pathname === '/registration.html') {
    const code = search.split('=')[1];
    if (code) return <Navigate to={`/registration/${code}`} replace />;
    return <Navigate to="/login" replace />;
  }

  // Handle /reset-password.html?=xxx
  if (pathname === '/reset-password.html') {
    const code = search.split('=')[1];
    if (code) return <Navigate to={`/reset-password/${code}`} replace />;
    return <Navigate to="/login" replace />;
  }

  // Handle query-string based routes: /?home, /?activity-reports, etc.
  const querystring = search.startsWith('?') ? search.substring(1) : '';

  if (querystring === 'signout') {
    signOut();
    return <Navigate to="/login" replace />;
  }

  const routeMap: Record<string, string> = {
    'home': '/home',
    'activity-reports': '/activity-reports',
    'case-reports': '/case-reports',
    'calls-for-service': '/calls-for-service',
    'analytics-reports': '/analytics-reports',
  };

  if (routeMap[querystring]) {
    return <Navigate to={routeMap[querystring]} replace />;
  }

  // Default: redirect based on auth state
  return <Navigate to={isAuthenticated ? '/home' : '/login'} replace />;
}
